import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { getProfile } from './store/authSlice';
import { useAppDispatch, useAppSelector } from './store/hooks';
import Login from './components/Auth/Login';
import OrgSelection from './components/Auth/OrgSelection';
import ChangePassword from './components/Auth/ChangePassword';
import AdminDashboardNew from './components/Admin/AdminDashboardNew';
import CreateUser from './components/Admin/CreateUser';
import EditUser from './components/Admin/EditUser';
import EditEvaluationWithAdminScore from './components/Admin/EditEvaluationWithAdminScore';
import AdminConfigHub from './components/Admin/AdminConfigHub';
import AdminPeopleHub from './components/Admin/AdminPeopleHub';
import AdminManagementHub from './components/Admin/AdminManagementHub';
import AdminEvaluationsPage from './components/Admin/AdminEvaluationsPage';
import EmployeeWorkspace from './components/Employee/EmployeeWorkspace';
import EvaluationForm from './components/Employee/EvaluationForm';
import ViewEvaluation from './components/Employee/ViewEvaluation';
import EvaluationsPage from './components/Employee/EvaluationsPage';
import ProjectForm from './components/Employee/ProjectForm';
import ProjectDetail from './components/Employee/ProjectDetail';
import TaskDetail from './components/Employee/TaskDetail';
import SprintForm from './components/Employee/Sprints/SprintForm';
import BugDetail from './components/Employee/Bugs/BugDetail';
import ProductDetail from './components/Admin/ProductDetail';
import ApplicationDetail from './components/Admin/ApplicationDetail';
import ProjectBoard from './components/Common/ProjectBoard';
import ExternalReviewSubmission from './components/Public/ExternalReviewSubmission';
import ApprovalByToken from './components/Approvals/ApprovalByToken';
import ApprovalsDashboard from './components/Approvals/ApprovalsDashboard';
import Settings from './components/Settings/Settings';
import KnowledgeBaseList from './components/KnowledgeBase/KnowledgeBaseList';
import KnowledgeBaseEditor from './components/KnowledgeBase/KnowledgeBaseEditor';
import KnowledgeBaseViewer from './components/KnowledgeBase/KnowledgeBaseViewer';
import OrganisationManagement from './components/Admin/OrganisationManagement';
import OrganisationDetail from './components/Admin/OrganisationDetail';
import PrivateRoute from './components/Common/PrivateRoute';
import Layout from './components/Common/Layout';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppContentInner() {
  const dispatch = useAppDispatch();
  const { token, user, requiresOrgSelection } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (token && !user && !requiresOrgSelection) {
      dispatch(getProfile());
    }
  }, [token, user, requiresOrgSelection, dispatch]);

  // Show loading spinner while fetching profile on page load/refresh.
  // Check before rendering routes so the default route never briefly sends
  // the user to /login while getProfile is in flight.
  if (token && !user && !requiresOrgSelection) {
    return (
      <div className="flex h-screen">
        <div className="w-14 bg-gray-900 shrink-0 hidden lg:block" />
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/select-org" element={<OrgSelection />} />

          <Route path="/review/:token" element={<ExternalReviewSubmission />} />
          <Route path="/approval/:token" element={<ApprovalByToken />} />

          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute minPriority={50}>
                <Layout>
                  <AdminDashboardNew />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/workspace"
            element={
              <PrivateRoute minPriority={50}>
                <Layout>
                  <EmployeeWorkspace />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/create-user"
            element={
              <PrivateRoute requireAdmin>
                <Layout>
                  <CreateUser />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/edit-user/:id"
            element={
              <PrivateRoute requireAdmin>
                <Layout>
                  <EditUser />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/evaluation/:id"
            element={
              <PrivateRoute requireAdmin>
                <Layout>
                  <EditEvaluationWithAdminScore />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Config hub — unified config page */}
          <Route
            path="/admin/config"
            element={
              <PrivateRoute requireAdmin>
                <Layout>
                  <AdminConfigHub />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Redirects from old config paths */}
          <Route path="/admin/categories/bulk-create" element={<Navigate to="/admin/config?tab=bulk-categories" replace />} />
          <Route path="/admin/categories" element={<Navigate to="/admin/config?tab=categories" replace />} />
          {/* People hub — users, departments, memberships */}
          <Route
            path="/admin/people"
            element={
              <PrivateRoute requireAdmin>
                <Layout>
                  <AdminPeopleHub />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route path="/admin/users" element={<Navigate to="/admin/people?tab=users" replace />} />
          <Route path="/admin/evaluation-types" element={<Navigate to="/admin/config?tab=eval-types" replace />} />
          <Route path="/admin/reviews" element={<Navigate to="/admin/management?tab=reviews" replace />} />

          <Route
            path="/employee/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <EmployeeWorkspace />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/employee/board"
            element={
              <PrivateRoute>
                <Layout>
                  <ProjectBoard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/employee/evaluations"
            element={
              <PrivateRoute>
                <Layout>
                  <EvaluationsPage />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Legacy redirect */}
          <Route path="/employee/open-evaluations" element={<Navigate to="/employee/evaluations" replace />} />
          <Route
            path="/employee/evaluation"
            element={
              <PrivateRoute>
                <Layout>
                  <EvaluationForm />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/employee/evaluation/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <ViewEvaluation />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Employee Project Routes — /projects redirects to dashboard */}
          <Route path="/employee/projects" element={<Navigate to="/employee/dashboard" replace />} />
          <Route
            path="/employee/projects/new"
            element={
              <PrivateRoute>
                <Layout>
                  <ProjectForm />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/employee/projects/:id/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <ProjectForm />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/employee/projects/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <ProjectDetail />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Sprint routes (standalone new-sprint page) */}
          <Route
            path="/employee/projects/:id/sprints/new"
            element={
              <PrivateRoute>
                <Layout>
                  <SprintForm projectId="" />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Bug standalone routes */}
          <Route
            path="/employee/bugs/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <BugDetail />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Employee Task Routes */}
          <Route
            path="/employee/tasks/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <TaskDetail />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Admin Evaluations */}
          <Route
            path="/admin/evaluations"
            element={
              <PrivateRoute minPriority={50}>
                <Layout>
                  <AdminEvaluationsPage />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Admin Project Routes */}
          {/* Management hub — projects, governance, reviews */}
          <Route
            path="/admin/management"
            element={
              <PrivateRoute minPriority={50}>
                <Layout>
                  <AdminManagementHub />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route path="/admin/projects" element={<Navigate to="/admin/management?tab=projects" replace />} />
          <Route
            path="/admin/projects/new"
            element={
              <PrivateRoute minPriority={50}>
                <Layout>
                  <ProjectForm />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/projects/:id/edit"
            element={
              <PrivateRoute minPriority={50}>
                <Layout>
                  <ProjectForm />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/projects/:id"
            element={
              <PrivateRoute minPriority={50}>
                <Layout>
                  <ProjectDetail />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Admin Product Routes */}
          <Route path="/admin/products" element={<Navigate to="/admin/management?tab=products" replace />} />
          <Route
            path="/admin/products/:id"
            element={
              <PrivateRoute requireAdmin>
                <Layout>
                  <ProductDetail />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Admin Application Routes */}
          <Route
            path="/admin/applications/:id"
            element={
              <PrivateRoute requireAdmin>
                <Layout>
                  <ApplicationDetail />
                </Layout>
              </PrivateRoute>
            }
          />

          <Route path="/admin/departments" element={<Navigate to="/admin/people?tab=departments" replace />} />

          {/* Redirects from old config paths */}
          <Route path="/admin/roles" element={<Navigate to="/admin/config?tab=roles" replace />} />
          <Route path="/admin/permissions" element={<Navigate to="/admin/config?tab=permissions" replace />} />
          <Route path="/admin/levels" element={<Navigate to="/admin/config?tab=levels" replace />} />

          {/* Organisation Routes — super-admin only */}
          <Route
            path="/admin/organisations"
            element={
              <PrivateRoute requireSuperAdmin>
                <Layout>
                  <OrganisationManagement />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/organisations/:id"
            element={
              <PrivateRoute requireSuperAdmin>
                <Layout>
                  <OrganisationDetail />
                </Layout>
              </PrivateRoute>
            }
          />

          <Route path="/admin/user-memberships" element={<Navigate to="/admin/people?tab=memberships" replace />} />

          <Route path="/admin/project-governance" element={<Navigate to="/admin/management?tab=governance" replace />} />

          {/* Project Board — moved to Management > Board tab */}
          {/* <Route path="/projects/board" element={<Navigate to="/admin/management?tab=board" replace />} /> */}

          {/* Approval Routes */}
          <Route
            path="/approvals"
            element={
              <PrivateRoute>
                <Layout>
                  <ApprovalsDashboard />
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Layout>
                  <Settings />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Knowledge Base Routes - visible to all authenticated users */}
          <Route
            path="/knowledge-base"
            element={
              <PrivateRoute>
                <Layout>
                  <KnowledgeBaseList />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/knowledge-base/new"
            element={
              <PrivateRoute>
                <Layout>
                  <KnowledgeBaseEditor />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/knowledge-base/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <KnowledgeBaseViewer />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/knowledge-base/:id/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <KnowledgeBaseEditor />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Default route - redirect based on authentication status and role */}
          <Route
            path="/"
            element={
              requiresOrgSelection ? (
                <Navigate to="/select-org" replace />
              ) : token && user ? (
                user.role === 'admin' || (user.organizationRole?.priority ?? 0) >= 50 ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : (
                  <Navigate to="/employee/dashboard" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
  );
}

function AppContent() {
  return (
    <Router>
      <ScrollToTop />
      <AppContentInner />
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
